// Memories Web Component
import ProtectedComponent from './protected-component.js';
import logger from '../utils/logger.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class Memories extends ProtectedComponent {
  static get observedAttributes() {
    return ['date'];
  }

  constructor() {
    super();
    this.memories = [];
    this.isLoading = false;
    this.error = null;
    this.currentDate = new Date();
    this.selectedMonth = this.currentDate.getMonth() + 1;
    this.selectedDay = this.currentDate.getDate();
    this.clickHandler = null; // Store reference to click handler for cleanup
    // Store references to other handlers for cleanup (though elements are recreated, this is safer)
    this.monthChangeHandler = null;
    this.dayChangeHandler = null;
    this.loadBtnHandler = null;
    this.retryBtnHandler = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.parseDateFromAttribute();
    this.render();
    this.loadMemories();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'date' && newValue !== oldValue) {
      this.parseDateFromAttribute();
      this.render();
      this.loadMemories();
    }
  }

  parseDateFromAttribute() {
    const dateAttr = this.getAttribute('date');
    if (dateAttr) {
      const dateMatch = dateAttr.match(/^(\d{1,2})-(\d{1,2})$/);
      if (dateMatch) {
        this.selectedMonth = parseInt(dateMatch[1], 10);
        this.selectedDay = parseInt(dateMatch[2], 10);
        return;
      }
    }
    // Default to today if no valid date attribute
    this.selectedMonth = this.currentDate.getMonth() + 1;
    this.selectedDay = this.currentDate.getDate();
  }

  render() {
    this.innerHTML = `
      <div class="memories-container">
        <div class="memories-header">
          ${renderPageTitle('🕰️', 'Memories')}
          <p class="memories-subtitle">Posts from this day in previous years</p>
        </div>

        <div class="memories-controls">
          <div class="date-selector">
            <label for="memory-month">Month:</label>
            <select id="memory-month" class="date-input">
              ${this.renderMonthOptions()}
            </select>
            <label for="memory-day">Day:</label>
            <select id="memory-day" class="date-input">
              ${this.renderDayOptions()}
            </select>
            <button class="btn btn-primary load-date-btn">Load Memories</button>
          </div>
        </div>

        <div class="memories-content">
          ${this.renderContent()}
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  renderMonthOptions() {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months.map((month, index) => {
      const monthNum = index + 1;
      return `<option value="${monthNum}" ${monthNum === this.selectedMonth ? 'selected' : ''}>${month}</option>`;
    }).join('');
  }

  renderDayOptions() {
    const daysInMonth = new Date(2024, this.selectedMonth, 0).getDate();
    const options = [];
    for (let day = 1; day <= daysInMonth; day++) {
      options.push(`<option value="${day}" ${day === this.selectedDay ? 'selected' : ''}>${day}</option>`);
    }
    return options.join('');
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading memories...</p>
        </div>
      `;
    }

    if (this.error) {
      return `
        <div class="error-state">
          <p class="error-message">${this.error}</p>
          <button class="btn btn-secondary retry-btn">Try Again</button>
        </div>
      `;
    }

    if (this.memories.length === 0) {
      const formattedDate = `${String(this.selectedMonth).padStart(2, '0')}-${String(this.selectedDay).padStart(2, '0')}`;
      return `
        <div class="empty-state">
          <p>No memories found for ${formattedDate}</p>
          <p class="empty-subtitle">Check back next year!</p>
        </div>
      `;
    }

    return `
      <div class="memories-list">
        ${this.memories.map((memory, index) => this.renderMemory(memory, index)).join('')}
      </div>
    `;
  }

  renderMemory(memory, index) {
    const postDate = new Date(memory.post_date);
    const year = memory.memory_year;
    const formattedDate = postDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
      <div class="memory-wrapper" data-memory-index="${index}" data-post-id="${memory.post_id}">
        <div class="memory-header-badge">
          <div class="memory-date-info">
            <span class="memory-year">${year}</span>
            <span class="memory-date">${formattedDate}</span>
          </div>
          <div class="memory-actions">
            <button class="btn btn-sm btn-outline share-memory-btn" 
                    data-post-id="${memory.post_id}" 
                    title="Share to main feed">
              📤 Share
            </button>
            <button class="btn btn-sm btn-outline hide-memory-btn" 
                    data-post-id="${memory.post_id}" 
                    title="Hide from memories">
              👁️ Hide
            </button>
          </div>
        </div>
        <post-item data-memory-index="${index}"></post-item>
      </div>
    `;
  }


  attachEventListeners() {
    // Remove existing click handler if it exists to prevent duplicate listeners
    if (this.clickHandler) {
      this.removeEventListener('click', this.clickHandler);
    }

    const monthSelect = this.querySelector('#memory-month');
    const daySelect = this.querySelector('#memory-day');
    const loadBtn = this.querySelector('.load-date-btn');
    const retryBtn = this.querySelector('.retry-btn');

    // Remove old handlers if they exist (elements are recreated, but this is safer)
    if (monthSelect && this.monthChangeHandler) {
      monthSelect.removeEventListener('change', this.monthChangeHandler);
    }
    if (daySelect && this.dayChangeHandler) {
      daySelect.removeEventListener('change', this.dayChangeHandler);
    }
    if (loadBtn && this.loadBtnHandler) {
      loadBtn.removeEventListener('click', this.loadBtnHandler);
    }
    if (retryBtn && this.retryBtnHandler) {
      retryBtn.removeEventListener('click', this.retryBtnHandler);
    }

    if (monthSelect) {
      this.monthChangeHandler = () => {
        this.selectedMonth = parseInt(monthSelect.value, 10);
        this.updateDayOptions();
      };
      monthSelect.addEventListener('change', this.monthChangeHandler);
    }

    if (daySelect) {
      this.dayChangeHandler = () => {
        this.selectedDay = parseInt(daySelect.value, 10);
      };
      daySelect.addEventListener('change', this.dayChangeHandler);
    }

    if (loadBtn) {
      this.loadBtnHandler = () => {
        // Update URL when date changes
        const dateStr = `${this.selectedMonth}-${this.selectedDay}`;
        window.history.pushState({}, '', `/memories/${dateStr}`);
        this.setAttribute('date', dateStr);
        this.loadMemories();
      };
      loadBtn.addEventListener('click', this.loadBtnHandler);
    }

    if (retryBtn) {
      this.retryBtnHandler = () => {
        this.loadMemories();
      };
      retryBtn.addEventListener('click', this.retryBtnHandler);
    }

    // Add event listener for share and hide buttons (store reference for cleanup)
    this.clickHandler = (e) => {
      if (e.target.classList.contains('share-memory-btn') || e.target.closest('.share-memory-btn')) {
        const btn = e.target.classList.contains('share-memory-btn') ? e.target : e.target.closest('.share-memory-btn');
        const postId = btn.getAttribute('data-post-id');
        this.shareMemoryToFeed(postId, btn);
      } else if (e.target.classList.contains('hide-memory-btn') || e.target.closest('.hide-memory-btn')) {
        const btn = e.target.classList.contains('hide-memory-btn') ? e.target : e.target.closest('.hide-memory-btn');
        const postId = btn.getAttribute('data-post-id');
        this.hideMemory(postId, btn);
      }
    };
    this.addEventListener('click', this.clickHandler);
  }

  updateDayOptions() {
    const daySelect = this.querySelector('#memory-day');
    if (!daySelect) return;

    const daysInMonth = new Date(2024, this.selectedMonth, 0).getDate();
    const currentDay = parseInt(daySelect.value, 10);

    daySelect.innerHTML = '';
    for (let day = 1; day <= daysInMonth; day++) {
      const option = document.createElement('option');
      option.value = day;
      option.textContent = day;
      if (day === currentDay || (day === this.selectedDay && day <= daysInMonth)) {
        option.selected = true;
        this.selectedDay = day;
      }
      daySelect.appendChild(option);
    }
  }

  async loadMemories() {
    this.isLoading = true;
    this.error = null;
    this.render();

    try {
      const dateStr = `${this.selectedMonth}-${this.selectedDay}`;
      const response = await window.API.request(`/memories?date=${dateStr}`, {
        method: 'GET'
      });

      if (response.memories) {
        this.memories = response.memories;
      } else {
        this.memories = [];
      }

      this.isLoading = false;
      this.render();

      // Set current user on post-item components and initialize them
      // Use multiple strategies to ensure custom elements are ready
      // First, try immediately (in case elements are already upgraded)
      this.initializePostItems();

      // Also try after a short delay to catch elements that upgrade later
      requestAnimationFrame(() => {
        this.initializePostItems();
      });

      // And try once more after a longer delay as a fallback
      setTimeout(() => {
        this.initializePostItems();
      }, 100);
    } catch (error) {
      logger.error('Error loading memories:', error);
      this.error = error.message || 'Failed to load memories';
      this.isLoading = false;
      this.render();
    }
  }

  initializePostItems() {
    // Set current user and post data on all post-item components
    // post-item will handle all image processing, gating, moderation, and missing image detection
    logger.debug('initializePostItems called', {
      hasCurrentUser: !!this.currentUser,
      memoriesCount: this.memories.length
    });

    if (!this.currentUser) {
      logger.warn('initializePostItems: No current user, skipping');
      return;
    }

    const postItems = this.querySelectorAll('post-item');
    logger.debug('Found post-item elements', { count: postItems.length });

    postItems.forEach((postItem, itemIndex) => {
      logger.debug(`Processing post-item ${itemIndex}`, {
        isConnected: postItem.isConnected,
        hasPostData: !!postItem._post,
        hasSetCurrentUser: typeof postItem.setCurrentUser === 'function',
        hasPostDataProperty: typeof postItem.postData !== 'undefined'
      });

      // Get the memory index from the data attribute first
      const memoryIndex = parseInt(postItem.getAttribute('data-memory-index'), 10);
      logger.debug(`post-item ${itemIndex} memory index:`, memoryIndex);

      if (isNaN(memoryIndex) || !this.memories[memoryIndex]) {
        logger.warn('post-item missing valid memory index or memory not found', {
          memoryIndex,
          memoriesLength: this.memories.length,
          memories: this.memories.map(m => ({ id: m.post_id, title: m.title }))
        });
        return;
      }

      // Get post data directly from memories array (more reliable than parsing attribute)
      const memory = this.memories[memoryIndex];
      logger.debug(`post-item ${itemIndex} memory data:`, {
        post_id: memory.post_id,
        title: memory.title,
        hasContentHtml: !!memory.content_html,
        hasContent: !!memory.content,
        contentHtmlLength: memory.content_html?.length || 0,
        contentLength: memory.content?.length || 0
      });

      const postData = {
        id: memory.post_id,
        title: memory.title || null,
        content: memory.content || '',
        content_html: memory.content_html || memory.content || '',
        username: memory.username,
        created_at: memory.post_date,
        is_owner: true, // Memories are always the user's own posts
        post_type: memory.post_type || 'text',
        source: memory.source || null,
        comment_count: 0, // Can be updated if needed
        mood_icon: memory.mood_icon || null,
        mood_status: memory.mood_status || null,
        music_url: memory.music_url || null,
        imported: memory.imported || false,
        import_source: memory.import_source || null,
        import_source_site: memory.import_source_site || null,
        import_source_url: memory.import_source_url || null
      };

      logger.debug(`post-item ${itemIndex} constructed postData:`, {
        id: postData.id,
        title: postData.title,
        hasContentHtml: !!postData.content_html,
        contentHtmlLength: postData.content_html?.length || 0,
        username: postData.username,
        post_type: postData.post_type
      });

      // Wait for element to be upgraded, then set both user and post data
      const setupPostItem = () => {
        logger.debug(`setupPostItem called for post-item ${itemIndex}`);

        if (!postItem) {
          logger.warn(`post-item ${itemIndex} is null`);
          return;
        }

        // Ensure element is in the DOM and upgraded
        if (!postItem.isConnected) {
          logger.warn(`post-item ${itemIndex} not connected to DOM yet`);
          return;
        }

        logger.debug(`post-item ${itemIndex} before setup:`, {
          hasPost: !!postItem._post,
          postId: postItem._post?.id,
          innerHTML: postItem.innerHTML.substring(0, 100)
        });

        // Set current user first (needed for image processing, gating, etc.)
        if (typeof postItem.setCurrentUser === 'function') {
          logger.debug(`Setting currentUser on post-item ${itemIndex}`);
          postItem.setCurrentUser(this.currentUser);
        } else {
          logger.warn(`post-item ${itemIndex} does not have setCurrentUser method`);
        }

        // Set post data via property (like posts-feed does) - this triggers render automatically
        // The property setter will call render() automatically
        try {
          logger.debug(`Setting postData on post-item ${itemIndex}`, { postId: postData.id });
          postItem.postData = postData;

          // Verify it was set
          const verifyPost = postItem._post || postItem.postData;
          logger.debug(`post-item ${itemIndex} after setting postData:`, {
            hasPost: !!verifyPost,
            postId: verifyPost?.id,
            matches: verifyPost?.id === postData.id,
            innerHTML: postItem.innerHTML.substring(0, 200)
          });

          if (!verifyPost || verifyPost.id !== postData.id) {
            logger.warn('postData was not set correctly, trying fallback', {
              expectedId: postData.id,
              actualId: verifyPost?.id,
              hasPost: !!verifyPost
            });
            // Fallback: set directly and trigger render
            postItem.postData = postData;
            if (typeof postItem.render === 'function') {
              logger.debug(`Manually calling render on post-item ${itemIndex}`);
              postItem.render().then(() => {
                logger.debug(`post-item ${itemIndex} render completed`, {
                  innerHTML: postItem.innerHTML.substring(0, 200)
                });
              }).catch(err => {
                logger.error('Error rendering post-item after fallback:', err);
              });
            } else {
              logger.warn(`post-item ${itemIndex} does not have render method`);
            }
          } else {
            logger.debug(`post-item ${itemIndex} postData set successfully`);
          }
        } catch (error) {
          logger.error('Error setting postData:', error, {
            postItemIndex: itemIndex,
            postId: postData.id,
            errorMessage: error.message,
            errorStack: error.stack
          });
          // Fallback: set directly and trigger render
          postItem._post = postData;
          if (typeof postItem.render === 'function') {
            logger.debug(`Calling render after error on post-item ${itemIndex}`);
            postItem.render().then(() => {
              logger.debug(`post-item ${itemIndex} render completed after error`, {
                innerHTML: postItem.innerHTML.substring(0, 200)
              });
            }).catch(err => {
              logger.error('Error rendering post-item after error fallback:', err);
            });
          }
        }
      };

      // Always wait for element to be defined to ensure it's fully upgraded
      // This ensures both connectedCallback and attributeChangedCallback have run
      logger.debug(`Waiting for post-item ${itemIndex} to be defined`);
      customElements.whenDefined('post-item').then(() => {
        logger.debug(`post-item ${itemIndex} is defined, scheduling setup`);
        // Use requestAnimationFrame to ensure the element is fully initialized
        requestAnimationFrame(() => {
          logger.debug(`requestAnimationFrame callback for post-item ${itemIndex}`);
          setupPostItem();
        });
      }).catch((error) => {
        logger.warn('Error waiting for post-item to be defined:', error);
        // Fallback: try to set up anyway after a delay
        setTimeout(() => {
          logger.debug(`Fallback timeout for post-item ${itemIndex}`);
          setupPostItem();
        }, 100);
      });
    });
  }

  async shareMemoryToFeed(postId, button) {
    if (!postId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Sharing...';

    try {
      const response = await window.API.request(`/memories/share/${postId}`, {
        method: 'POST'
      });

      if (response) {
        button.textContent = '✓ Shared';
        button.classList.add('shared');
        // Show success message
        this.showMessage('Memory shared to feed!', 'success');
      }
    } catch (error) {
      logger.error('Error sharing memory:', error);
      button.textContent = originalText;
      button.disabled = false;
      this.showMessage(error.message || 'Failed to share memory', 'error');
    }
  }

  async hideMemory(postId, button) {
    if (!postId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Hiding...';

    try {
      const response = await window.API.request(`/memories/hide/${postId}`, {
        method: 'POST'
      });

      if (response) {
        // Remove the memory from the list
        const memoryWrapper = button.closest('.memory-wrapper');
        if (memoryWrapper) {
          memoryWrapper.style.opacity = '0';
          memoryWrapper.style.transition = 'opacity 0.3s';
          setTimeout(() => {
            memoryWrapper.remove();
            // Update memories array to reflect removal (no need to reload from server)
            const postIdInt = parseInt(postId, 10);
            this.memories = this.memories.filter(m => m.post_id !== postIdInt);
          }, 300);
        }
        this.showMessage('Memory hidden from memories', 'success');
      }
    } catch (error) {
      logger.error('Error hiding memory:', error);
      button.textContent = originalText;
      button.disabled = false;
      this.showMessage(error.message || 'Failed to hide memory', 'error');
    }
  }

  showMessage(message, type = 'info') {
    // Create a temporary message element
    const messageEl = document.createElement('div');
    messageEl.className = `memory-message memory-message-${type}`;
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${type === 'success' ? 'var(--success-color, #4caf50)' : 'var(--error-color, #f44336)'};
      color: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease-out;
    `;

    document.body.appendChild(messageEl);

    setTimeout(() => {
      messageEl.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => {
        messageEl.remove();
      }, 300);
    }, 3000);
  }
}

customElements.define('memories-view', Memories);
export default Memories;

